import { instance } from '@/services/api/index';

/**
 * Service für Dashboard-bezogene API-Aufrufe
 */
export const dashboardService = {
  /**
   * Holt die Dashboard-Statistiken vom Server und transformiert sie in das
   * vom Frontend benötigte Format
   * @param {boolean} useCache - Optional, ob gecachte Daten verwendet werden sollen
   * @returns {Promise} Promise mit den Dashboard-Daten
   */
  getStats: async (useCache = true) => {
    try {
      // Timeout für den API-Aufruf setzen (5 Sekunden)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await instance.get('/Dashboard/stats', {
        signal: controller.signal,
        // Cache-Einstellungen für wiederholte Anfragen
        headers: useCache ? {} : { 'Cache-Control': 'no-cache' }
      });
      
      clearTimeout(timeoutId);
      
      // Speichern im sessionStorage für Offline-Fallback
      try {
        sessionStorage.setItem('dashboardStats', JSON.stringify(response.data));
        sessionStorage.setItem('dashboardStatsTimestamp', Date.now().toString());
      } catch (storageError) {
        console.warn('Konnte Daten nicht im Cache speichern:', storageError);
      }
      
      return transformBackendData(response.data);
    } catch (error) {
      console.error('Fehler beim Abrufen der Dashboard-Statistiken:', error);
      
      // Bei Netzwerkfehler: Versuche Daten aus dem Session Storage zu laden
      if (error.message === 'Network Error' || error.code === 'ECONNABORTED' || error.name === 'AbortError') {
        try {
          const cachedData = sessionStorage.getItem('dashboardStats');
          const timestamp = sessionStorage.getItem('dashboardStatsTimestamp');
          
          if (cachedData) {
            console.log('Verwende gecachte Dashboard-Daten vom', new Date(parseInt(timestamp || 0)).toLocaleString());
            const parsedData = JSON.parse(cachedData);
            return transformBackendData(parsedData);
          }
        } catch (cacheError) {
          console.warn('Konnte keine gecachten Daten laden:', cacheError);
        }
      }
      
      // Wenn kein Cache verfügbar ist, einen leeren Datensatz zurückgeben
      // Dies verhindert App-Abstürze und zeigt stattdessen leere States an
      return transformBackendData({
        metadata: {
          lastUpdatedAt: new Date().toISOString(),
          coveredPeriod: {
            startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date().toISOString()
          }
        }
      });
    }
  },
  
  /**
   * Erzwingt eine Aktualisierung der Daten ohne Cache
   */
  refreshStats: async () => {
    return dashboardService.getStats(false);
  }
};

/**
 * Transformiert die Backend-Daten in das vom Frontend benötigte Format
 * @param {Object} data - Die Backend-Daten
 * @returns {Object} Transformierte Daten für das Frontend
 */
const transformBackendData = (data) => {
  if (!data) return getEmptyState();

  const activeOrders = data.activeOrders;
  const activeOrdersCount = typeof activeOrders === 'object' && activeOrders !== null
    ? (activeOrders.value ?? 0)
    : (Number(activeOrders) || 0);

  const totalRevenue = data.totalRevenue;
  const todaySales = typeof totalRevenue === 'object' && totalRevenue !== null
    ? (totalRevenue.value ?? 0)
    : (Number(totalRevenue ?? data.todayRevenue) || 0);

  const totalOrders = data.totalOrders;
  const todayOrdersCount = typeof totalOrders === 'object' && totalOrders !== null
    ? (totalOrders.value ?? 0)
    : (Number(totalOrders) || 0);
  const ordersChangePercentage = typeof totalOrders === 'object' && totalOrders !== null
    ? (totalOrders.changePercentage ?? 0)
    : 0;
  const revenueChangePercentage = typeof totalRevenue === 'object' && totalRevenue !== null
    ? (totalRevenue.changePercentage ?? 0)
    : 0;

  const top5Raw = data.top5Products || data.topProducts || [];
  const top5Products = Array.isArray(top5Raw)
    ? top5Raw.map((p) => ({
        productName: p.productName ?? p.product_name ?? '–',
        soldCount: p.soldCount ?? p.total_sold ?? 0,
        revenue: Number(p.revenue ?? p.total_revenue ?? 0)
      }))
    : [];

  const topProduct = data.topProduct;
  const topProductName = (topProduct?.productName ?? top5Products[0]?.productName) || 'N/A';
  const topProductCount = (topProduct?.value ?? top5Products[0]?.soldCount) ?? 0;

  const revenueTrendRaw = data.revenueTrend || [];
  const orderTrendRaw = data.orderTrend || [];

  return {
    lastUpdate: data.metadata?.lastUpdatedAt ?? new Date().toISOString(),
    activeOrdersCount,
    todayOrdersCount,
    todaySales,
    ordersChangePercentage,
    revenueChangePercentage,
    tableStatus: {
      total: data.totalTables ?? 0,
      occupied: data.occupiedTables ?? 0,
      occupancyPercentage: data.tableOccupancyPercentage ?? 0
    },
    topProductName,
    topProductCount,
    topProductUnit: topProduct?.unit ?? 'Stück',
    revenueTrend: revenueTrendRaw.map((item) => ({
      date: item.label ?? item.date,
      amount: Number(item.value ?? item.revenue ?? 0)
    })),
    orderTrend: orderTrendRaw.map((item) => ({
      date: item.label ?? item.date,
      count: Number(item.value ?? item.orders ?? 0)
    })),
    categoryPerformance: Array.isArray(data.categoryPerformance) ? data.categoryPerformance : [],
    top5Products,
    ordersByHourTrend: data.ordersByHourTrend || []
  };
};

/**
 * Liefert ein leeres Daten-Objekt für Fallback
 */
const getEmptyState = () => ({
  lastUpdate: new Date().toISOString(),
  period: { 
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), 
    endDate: new Date().toISOString() 
  },
  activeOrdersCount: 0,
  todayOrdersCount: 0,
  todaySales: 0,
  ordersChangePercentage: 0,
  revenueChangePercentage: 0,
  tableStatus: {
    total: 0,
    occupied: 0,
    occupancyPercentage: 0
  },
  topProductName: 'N/A',
  topProductCount: 0,
  topProductUnit: 'Stück',
  revenueTrend: [],
  orderTrend: [],
  categoryPerformance: [],
  top5Products: [],
  ordersByHourTrend: []
});

export default dashboardService; 